// Match-level state that lives across rounds. Owned by LocalTransport.
// World only knows about one round at a time — MatchState tracks the roster,
// crown counts, round number, and which character the camera is focused on
// (for spectate mode when the local player dies mid-round).

import type { HatId } from '../render/HatModels';
import type { TeamId } from './SimState';

export const ROUNDS_TO_WIN = 3;

/** One slot in the match roster — four of these, two per team. */
export interface TeamMember {
  /** Sim character id (0..3). 0 = local human. */
  id: number;
  name: string;
  /** Base hex color the character's toon palette is derived from. */
  color: string;
  hatId: HatId | null;
  team: TeamId;
  isLocal: boolean;
}

export type MatchPhase = 'playing' | 'roundOver' | 'gameOver';

export interface CrownCounts {
  red: number;
  blue: number;
}

/** The match-level state exposed to UI layers (HUD / modals). */
export class MatchState {
  members: TeamMember[];
  round = 1;
  crowns: CrownCounts = { red: 0, blue: 0 };
  phase: MatchPhase = 'playing';
  lastRoundWinner: TeamId | null = null;
  /** Crowns for each team BEFORE the latest-round increment was applied —
   *  used by the round-end modal to animate the newly-won crown filling in. */
  crownsBeforeLatestWin: CrownCounts = { red: 0, blue: 0 };
  /** Which character the camera follows. Starts on the local player, switches
   *  to teammate on local-player death, and cycles via spectate input. */
  focusId: number;

  constructor(members: TeamMember[]) {
    if (members.length !== 4) {
      throw new Error(`MatchState needs exactly 4 members, got ${members.length}`);
    }
    this.members = members;
    const local = members.find((m) => m.isLocal);
    this.focusId = local ? local.id : members[0]!.id;
  }

  memberById(id: number): TeamMember | undefined {
    return this.members.find((m) => m.id === id);
  }

  membersOfTeam(team: TeamId): TeamMember[] {
    return this.members.filter((m) => m.team === team);
  }

  teamOf(id: number): TeamId {
    const m = this.memberById(id);
    if (!m) throw new Error(`No team for character id ${id}`);
    return m.team;
  }

  teamByIdMap(): Record<number, TeamId> {
    const out: Record<number, TeamId> = {};
    for (const m of this.members) out[m.id] = m.team;
    return out;
  }

  localPlayer(): TeamMember {
    return this.members.find((m) => m.isLocal)!;
  }

  localTeam(): TeamId {
    return this.localPlayer().team;
  }

  /** Award a crown to the given team (or record a draw when null). Returns
   *  true if that crown pushed a team over the win threshold. */
  applyRoundWin(winner: TeamId | null): boolean {
    this.crownsBeforeLatestWin = { red: this.crowns.red, blue: this.crowns.blue };
    this.lastRoundWinner = winner;
    if (winner === null) {
      this.phase = 'roundOver';
      return false;
    }
    this.crowns[winner] += 1;
    const gameOver = this.crowns[winner] >= ROUNDS_TO_WIN;
    this.phase = gameOver ? 'gameOver' : 'roundOver';
    return gameOver;
  }

  /** Called after the round-end modal dismisses — bump the round counter and
   *  put the match back into playing phase. No-op if the match is already
   *  over (the game-end modal handles that case via the Back-to-Lobby path). */
  advanceRound(): void {
    if (this.phase !== 'roundOver') return;
    this.round += 1;
    this.phase = 'playing';
  }
}

/** Build a 4-member roster for the local-multiplayer mode: the human player
 *  plus one AI teammate on their team, and two AI opponents on the other.
 *  Team assignment for the local player is randomized. NPC names + colors
 *  are picked from the pools, avoiding duplicates with the player. */
export function buildLocalRoster(
  localProfile: { name: string; color: string; hatId: HatId | null },
  npcColorPool: string[],
  randomNpcName: () => string
): TeamMember[] {
  const localTeam: TeamId = Math.random() < 0.5 ? 'red' : 'blue';
  const enemyTeam: TeamId = localTeam === 'red' ? 'blue' : 'red';

  const usedColors = new Set<string>([localProfile.color.toLowerCase()]);
  const usedNames = new Set<string>([localProfile.name]);
  const pool = npcColorPool.filter((c) => !usedColors.has(c.toLowerCase()));
  // Shuffle the pool once so color assignment is non-deterministic, then
  // walk it picking the next unused color for each NPC.
  for (let i = pool.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [pool[i], pool[j]] = [pool[j]!, pool[i]!];
  }
  const pickColor = (idx: number): string => pool[idx % pool.length] ?? '#cccccc';
  const pickName = (): string => {
    for (let i = 0; i < 8; i++) {
      const n = randomNpcName();
      if (!usedNames.has(n)) { usedNames.add(n); return n; }
    }
    // Fallback: append a counter if we collided repeatedly.
    let n = randomNpcName();
    let k = 2;
    while (usedNames.has(n)) { n = `${n} ${k++}`; }
    usedNames.add(n);
    return n;
  };

  const members: TeamMember[] = [
    { id: 0, name: localProfile.name, color: localProfile.color, hatId: localProfile.hatId, team: localTeam, isLocal: true },
    { id: 1, name: pickName(), color: pickColor(0), hatId: null, team: localTeam, isLocal: false },
    { id: 2, name: pickName(), color: pickColor(1), hatId: null, team: enemyTeam, isLocal: false },
    { id: 3, name: pickName(), color: pickColor(2), hatId: null, team: enemyTeam, isLocal: false }
  ];
  return members;
}

/** Team-color tokens used across the HUD, modals, and name tags. */
export const TEAM_COLORS: Record<TeamId, { primary: string; accent: string; label: string }> = {
  red:  { primary: '#FF4D4D', accent: '#7A2A0E', label: 'RED' },
  blue: { primary: '#3DA6FF', accent: '#0E3A66', label: 'BLUE' }
};
